import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useMessageList } from '../Context/MessageListContext';
import { 
  sendTextMessage, 
  sendMediaMessage, 
  getConversationMessages, 
  markConversationAsRead,
  getConversations 
} from '@api/whatsappApi';
import apis from '@api/api';
import { IMessage } from 'react-native-gifted-chat';

interface SendMessageParams {
	to: string;
	message: string;
	conversationId?: string;
	messageType?: 'text' | 'media';
	mediaUrl?: string;
	mediaType?: 'image' | 'video' | 'audio' | 'document';
	caption?: string;
	businessPhoneNumberId?: string;
}

interface UseContextualMessagingReturn {
	sendMessage: (params: SendMessageParams) => Promise<any>;
	getMessagesForConversation: (
		conversationId: string,
		page?: number,
		limit?: number
	) => Promise<IMessage[]>;
	markAsRead: (conversationId: string, messageId?: string) => Promise<void>;
	isWhatsAppContext: boolean;
	getConversationId: (item: any) => string;
	formatUserForDisplay: (user: any) => any;
}

export const useContextualMessaging = (): UseContextualMessagingReturn => {
	const { state, dispatch } = useMessageList();
	const activeFilter = useSelector(
		(state: DefaultRootState) => state.UI.messageAppFilter
	);
	const activeWhatsappNumber = useSelector(
		(state: DefaultRootState) =>
			state.UI.messageOptions?.activeWhatsappNumber
	);
	
	// Get authenticated user for GiftedChat alignment (only for WhatsApp context)
	const authenticatedUser = useSelector((state: DefaultRootState) => state.promiseData.data.userData);
	const authenticatedUserId = authenticatedUser?._id || authenticatedUser?.id || authenticatedUser?.userId || 'current-user';

	const isWhatsAppContext = activeFilter === 'whatsapp';

	const sendMessage = async (params: SendMessageParams) => {
		const {
			to,
			message,
			conversationId,
			messageType = 'text',
			mediaUrl,
			mediaType,
			caption,
			businessPhoneNumberId,
		} = params;

		console.log(
			'📱 useContextualMessaging - sendMessage called with params:',
			{
				to,
				message,
				conversationId,
				messageType,
				isWhatsAppContext,
				activeWhatsappNumber,
				toType: typeof to,
				toValue: to,
				phoneNumberValidation: {
					hasTo: !!to,
					toLength: to?.length,
					toFormat: to?.includes('+') ? 'international' : 'local',
					hasActiveNumber: !!activeWhatsappNumber,
					activeNumberFormat: activeWhatsappNumber?.includes('+')
						? 'international'
						: 'local',
				},
			}
		);

		try {
			if (isWhatsAppContext) {
				// Send WhatsApp message
				if (messageType === 'text') {
					const response = await sendTextMessage({
						to,
						message,
						businessPhoneNumberId:
							businessPhoneNumberId ||
							activeWhatsappNumber ||
							'123456123',
					});

					// Update conversation status
					if (conversationId) {
						dispatch({
							type: 'UPDATE_WHATSAPP_STATUS',
							payload: { conversationId, status: 'sent' },
						});
					}

					return response;
				} else if (messageType === 'media' && mediaUrl && mediaType) {
					const response = await sendMediaMessage({
						to,
						mediaUrl,
						mediaType,
						caption: caption || message,
						businessPhoneNumberId:
							businessPhoneNumberId ||
							activeWhatsappNumber ||
							'123456123',
					});

					if (conversationId) {
						dispatch({
							type: 'UPDATE_WHATSAPP_STATUS',
							payload: { conversationId, status: 'sent' },
						});
					}

					return response;
				}
			} else {
				// Send internal message
				const response = await apis.postMessage(to, {
					participantId: to,
					message,
					conversationId,
				});

				// Update internal message status
				dispatch({
					type: 'UPDATE_STATUS',
					userId: to,
					status: 'sent',
				});

				return response;
			}
		} catch (error) {
			console.error('Failed to send message:', error);

			// Update status to failed
			if (isWhatsAppContext && conversationId) {
				dispatch({
					type: 'UPDATE_WHATSAPP_STATUS',
					payload: { conversationId, status: 'failed' },
				});
			} else {
				dispatch({
					type: 'UPDATE_STATUS',
					userId: to,
					status: 'failed',
				});
			}

			throw error;
		}
	};

	const getMessagesForConversation = async (
		conversationId: string,
		page: number = 1,
		limit: number = 50
	): Promise<IMessage[]> => {
		try {
			if (isWhatsAppContext) {
				// Get WhatsApp messages
				console.log(
					'📱 getMessagesForConversation - Fetching messages:',
					{ conversationId, page, limit }
				);
				const response = await getConversationMessages(conversationId, {
					page,
					limit,
				});
				console.log(
					'📱 getMessagesForConversation - Full API response:',
					{
						responseType: typeof response,
						responseKeys: Object.keys(response || {}),
						dataType: typeof response.data,
						dataKeys: Object.keys(response.data || {}),
						hasMessages: !!response.data?.messages,
						messagesType: typeof response.data?.messages,
						messagesIsArray: Array.isArray(response.data?.messages),
						messagesLength: response.data?.messages?.length,
						firstMessage: response.data?.messages?.[0],
						rawResponse: response,
					}
				);
				const messages = response.data?.messages || [];
				console.log(
					'📱 getMessagesForConversation - Extracted messages:',
					{
						messagesLength: messages.length,
						messagesType: typeof messages,
						messagesIsArray: Array.isArray(messages),
						firstMessage: messages[0],
					}
				);

				// Helper function to extract text content from message
				const extractMessageText = (msg: any): string => {
					// Handle template messages
					if (msg.type === 'template') {
						const templateName = msg.content?.template?.name || msg.content?.name || 'Template';
						const templateLanguage = msg.content?.template?.language || msg.content?.language || 'en';
						
						// Extract parameter values from template components
						// Parameters can be stored as components array or directly as parameters
						const components = msg.content?.template?.components || msg.content?.template?.parameters || [];
						const parameterTexts: string[] = [];
						
						components.forEach((component: any) => {
							// Component can have parameters array (WhatsApp API format)
							if (component.parameters && Array.isArray(component.parameters)) {
								component.parameters.forEach((param: any) => {
									if (param.text) {
										parameterTexts.push(param.text);
									}
								});
							}
							// Or component can be a direct parameter object
							else if (component.text) {
								parameterTexts.push(component.text);
							}
						});
						
						// Format template message display
						if (parameterTexts.length > 0) {
							return `📝 ${templateName} (${templateLanguage})\n${parameterTexts.join('\n')}`;
						}
						return `📝 ${templateName} (${templateLanguage})`;
					}
					
					// Handle other message types
					// Ensure we always return a string, not an array
					const textContent = msg.content?.body || msg.content?.text || msg.media?.caption || msg.content?.template?.name || msg.text || '';
					
					// If textContent is an array, join it (shouldn't happen, but safety check)
					if (Array.isArray(textContent)) {
						console.warn('⚠️ Message text is an array, joining:', textContent);
						return textContent.join('');
					}
					
					// Ensure it's a string
					return String(textContent);
				};

				// Convert WhatsApp messages to GiftedChat format
				const transformedMessages = messages.map(
					(msg: any): IMessage => {
					// Ensure user._id is always a string (required by GiftedChat)
					// For outbound messages, use authenticated user ID to match currentUser._id (for GiftedChat alignment only)
					// For inbound messages, use the phone number (from) so it doesn't match currentUser
					const userId = msg.direction === 'outbound'
						? String(authenticatedUserId)  // Must match currentUser._id for right alignment
						: (msg.from ? String(msg.from) : 'unknown');
						
					// Ensure user.name is always a string
					// For inbound messages, prioritize contactInfo from database, then from message data
					const userName = msg.direction === 'outbound'
						? (authenticatedUser?.profile_information?.name || authenticatedUser?.name || 'You')
						: (msg.contactInfo?.name || msg.contactInfo?.profileName || msg.contactName || (msg.from ? String(msg.from) : 'Unknown Contact'));
						
					// Extract text and ensure it's a string (not an array)
					let messageText = extractMessageText(msg);
					
					// Safety check: if text is an array, join it
					if (Array.isArray(messageText)) {
						console.warn('⚠️ Message text is an array, joining:', {
							messageId: msg.messageId || msg._id,
							textArray: messageText,
							joined: messageText.join('')
						});
						messageText = messageText.join('');
					}
					
					// Ensure it's always a string
					messageText = String(messageText);
					
					const transformedMessage: IMessage = {
						_id: msg.messageId || msg._id || String(msg._id), // Use messageId from database schema, ensure it's a string
						text: messageText,
						createdAt: new Date(msg.timestamp), // timestamp is already a Date object
							user: {
								_id: userId, // Always a string, required by GiftedChat
								name: userName, // Always a string
								avatar: msg.contactProfilePicture || undefined,
							},
							image:
								msg.media?.url && msg.type === 'image'
									? msg.media.url
									: undefined,
							video:
								msg.media?.url && msg.type === 'video'
									? msg.media.url
									: undefined,
							audio:
								msg.media?.url && msg.type === 'audio'
									? msg.media.url
									: undefined,
							system: msg.type === 'system',
						};
						
						// Debug logging for alignment
						if (__DEV__) {
							console.log('📱 Message transformation:', {
								messageId: transformedMessage._id,
								direction: msg.direction,
								userId: transformedMessage.user._id,
								userName: transformedMessage.user.name,
								from: msg.from,
								to: msg.to,
								authenticatedUserId: String(authenticatedUserId),
								willAlignRight: transformedMessage.user._id === String(authenticatedUserId),
								userIdType: typeof transformedMessage.user._id,
								userIdValue: transformedMessage.user._id
							});
						}
						
						return transformedMessage;
					}
				);

				// Sort messages by timestamp (newest first, GiftedChat will reverse for display)
				const sortedMessages = transformedMessages.sort(
					(a, b) =>
						new Date(b.createdAt).getTime() -
						new Date(a.createdAt).getTime()
				);

				console.log(
					'📱 getMessagesForConversation - Transformed and sorted messages:',
					sortedMessages.length
				);
				return sortedMessages;
			} else {
				// Get internal messages (using getConversation API)
				const response = await apis.getConversation({
					conversationId,
					page,
					limit,
				});
				const messages = response.data?.messages || [];

				// Convert internal messages to GiftedChat format
				return messages.map(
					(msg: any): IMessage => ({
						_id: msg._id,
						text: msg.message || msg.text,
						createdAt: new Date(msg.timestamp || msg.createdAt),
						user: {
							_id: msg.senderId || msg.from,
							name: msg.senderName || msg.user?.name,
							avatar: msg.senderAvatar || msg.user?.avatar,
						},
					})
				);
			}
		} catch (error) {
			console.error('Failed to get messages:', error);
			return [];
		}
	};

	const markAsRead = async (conversationId: string, messageId?: string) => {
		try {
			if (isWhatsAppContext) {
				// Mark WhatsApp message as read
				if (messageId) {
					await markConversationAsRead(conversationId);
				}

				dispatch({
					type: 'MARK_WHATSAPP_READ',
					payload: { conversationId, messageId: messageId || '' },
				});
			} else {
				// Mark internal message as read (using markRead function)
				await apis.markRead(conversationId, ''); // markRead expects accessToken as second param

				dispatch({
					type: 'UPDATE_BADGE',
					userId: conversationId,
					amount: -999, // Reset badge to 0
					event: { type: 'read' },
				});
			}
		} catch (error) {
			console.error('Failed to mark as read:', error);
		}
	};

	const getConversationId = (item: any): string => {
		if (isWhatsAppContext) {
			// WhatsApp conversation ID
			return item.id || item.conversationId || item._id;
		} else {
			// Internal conversation ID
			return item.conversationId || item._id;
		}
	};

	const formatUserForDisplay = (user: any) => {
		if (isWhatsAppContext) {
			// Format WhatsApp conversation for display
			return {
				_id: user.id || user._id,
				name:
					user.contactName ||
					user.participantPhone ||
					'Unknown Contact',
				surname: '', // WhatsApp doesn't typically have separate surname
				avatar: { img: user.contactProfilePicture || '' },
				phone: user.participantPhone,
				status: user.status,
				lastMessage: user.lastMessage,
				lastMessageTime: user.lastMessageTime,
				badge: user.unreadCount || 0,
				conversationId: user.id,
				profile_information: {
					name:
						user.contactName ||
						user.participantPhone ||
						'Unknown Contact',
					surname: '',
					avatar: { img: user.contactProfilePicture || '' },
				},
				// Additional WhatsApp-specific fields
				businessPhoneNumberId: user.businessPhoneNumberId,
				messageCount: user.messageCount,
				tags: user.tags || [],
				assignedTo: user.assignedTo,
			};
		} else {
			// Internal user format (already in expected format)
			return {
				...user,
				badge: user.badge || 0,
				conversationId: user.conversationId || user._id,
			};
		}
	};

	return {
		sendMessage,
		getMessagesForConversation,
		markAsRead,
		isWhatsAppContext,
		getConversationId,
		formatUserForDisplay,
	};
};